import { Button, Container, Form, LayoutGrid, Spinner } from '@asktia/tia-ui'
import { get as _get, omit as _omit } from 'lodash'
import { FC, useEffect, useState } from 'react'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import { GluedDisplayQuestion } from 'src/flows/questionnaires/components/Input/Display'
import { GluedLongOpenInputQuestion } from 'src/flows/questionnaires/components/Input/LongOpenInput'
import { useQuestionnaire } from 'src/flows/questionnaires/hooks/useQuestionnaire'
import { LegalDisclaimer } from 'src/flows/questionnaires/MentalHealth/components/LegalDisclaimer'
import { useNavigation } from '../../../../../hooks'
import { ErrorSavingBanner } from '../../../components/ErrorSavingBanner'
import { GluedBooleanInputQuestion } from '../../../components/Input/BooleanInput'
import { GluedMultiChoiceQuestion } from '../../../components/Input/MultiChoice'
import { GluedOpenInputQuestion } from '../../../components/Input/OpenInput'
import { GluedSingleChoiceQuestion } from '../../../components/Input/SingleChoice'
import { QuestionnaireDivider } from '../../../components/QuestionnaireDivider'
import { getFormDone } from '../../../getFormDone'
import { useQuestionnaireResponse } from '../../../hooks/useQuestionnaireResponse'
import { ErrorSubmittingBanner } from 'src/flows/questionnaires/components/ErrorSubmittingBanner'
import { useSubmitQuestionnaireResponse } from 'src/flows/questionnaires/hooks/open-loop/useSubmitQuestionnaire'
import { QuestionContainer } from 'src/flows/questionnaires/components/QuestionContainer'
import { useCommunicationRequest } from 'src/flows/questionnaires/hooks/open-loop/useCommunicationRequest'
import { NavBarHeading } from 'src/components/Blocks/NavBarHeading'

const questionnaireId = 'ol-glp1-refill'

export const OLGLP1RefillPage: FC = () => {
    const formMethods = useForm<FieldValues>({
        defaultValues: {},
        mode: 'all'
    })

    const [searchParams] = useSearchParams()
    const { navigate } = useNavigation()
    const [redirectPage, setRedirectPage] = useState<string | undefined>(
        undefined
    )
    const [showSavingError, setShowSavingError] = useState(false)
    const [showSubmittingError, setShowSubmittingError] = useState(false)
    const { questionnaire, isLoading: isQuestionnaireLoading } =
        useQuestionnaire(questionnaireId)
    const { formDone } = getFormDone(formMethods.getValues())
    const { submitQuestionnaireResponse } = useSubmitQuestionnaireResponse()
    const {
        updateCommunicationRequestStatus,
        communicationRequest,
        isLoading: isComReqLoading,
        isSubmitting
    } = useCommunicationRequest()
    const {
        answersMap,
        questionnaireResponse,
        saveQuestionnaireResponse,
        isLoading: isQuestionnaireResponseLoading
    } = useQuestionnaireResponse(
        'ol-glp1-refill',
        searchParams.get('appointmentProfileUuid') as string
    )

    useEffect(() => {
        if (redirectPage) {
            navigate(redirectPage)
            setRedirectPage(undefined)
        }
    }, [redirectPage, navigate])

    if (
        isComReqLoading ||
        isQuestionnaireResponseLoading ||
        isQuestionnaireLoading
    ) {
        return <Spinner />
    }

    if (!communicationRequest) {
        return (
            <>
                <NavBarHeading href="/your-care" />
                <Container>
                    <LayoutGrid columns="one">
                        We don’t need you to fill out a refill intake at this
                        time.
                    </LayoutGrid>
                </Container>
            </>
        )
    }

    if (communicationRequest.status === 'completed') {
        navigate('next-steps')
        return <></>
    }

    const getValidFormValues = async (triggeredByFieldName?: string) => {
        await formMethods.trigger(triggeredByFieldName)

        const formValues = formMethods.getValues()
        const formKeys = Object.keys(formValues)

        const validFormValues: any = {}
        const fieldErrors = formMethods.formState.errors

        formKeys.forEach((key: string) => {
            const fieldValue = _get(formValues, key)
            const nestedFieldErrors = _get(fieldErrors, key)
            validFormValues[key as string] =
                typeof fieldValue === 'object' && !Array.isArray(fieldValue)
                    ? _omit(fieldValue, Object.keys(nestedFieldErrors || {}))
                    : fieldValue
        })

        return validFormValues
    }

    async function triggerSave(triggeredByFieldName?: string) {
        setShowSavingError(false)
        try {
            const validValues = await getValidFormValues(triggeredByFieldName)
            await saveQuestionnaireResponse(validValues)
            if (communicationRequest) {
                const currentStatus =
                    communicationRequest.statusReason?.coding?.[0]?.code
                if (currentStatus === 'ready') {
                    updateCommunicationRequestStatus(communicationRequest)
                }
            }
            return true
        } catch (e) {
            if (triggeredByFieldName) {
                formMethods.setValue(triggeredByFieldName, undefined)
            }
            setShowSavingError(true)
            return false
        }
    }

    const onComplete = async () => {
        setShowSubmittingError(false)
        await triggerSave()
        try {
            await submitQuestionnaireResponse(questionnaireId)
            // using the navigate here didn't work because the QuestionnaireResponse is reloaded after the
            // submission, so page got re-rendered and the redirect was lost
            // so we set the redirectPage state and useEffect will handle the navigation
            setRedirectPage('next-steps')
        } catch (error) {
            setShowSubmittingError(true)
        }
    }

    const questionFormGlue = {
        questionnaire,
        questionnaireResponse,
        answersMap,
        triggerSave
    }

    const canProceed = formDone && Object.keys(formMethods.errors).length === 0

    return (
        <>
            {showSubmittingError && <ErrorSubmittingBanner />}
            {showSavingError && <ErrorSavingBanner />}
            <LayoutGrid columns="one" variant="condensed">
                <FormProvider {...formMethods}>
                    <Form useFormMethods={formMethods} onSubmit={() => {}}>
                        <QuestionContainer>
                            {/* Have there been any changes to your medical,
                            social, or surgical history since your last visit
                            with us? */}
                            <GluedSingleChoiceQuestion
                                linkId="q-medhistory-changes"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Do any of the following apply to you?
                            (enableWhen: q-medhistory-changes = 'Yes') */}
                            <GluedMultiChoiceQuestion
                                linkId="q-medhistory-followup"
                                variant="info"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Please explain the interval changes in medical, social, or surgical history since your last visit.
                            (enableWhen: q-medhistory-followup = 'None of the above') */}
                            <GluedOpenInputQuestion
                                linkId="q-medhistory-followup-explanation"
                                variant="info"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Are you currently taking, plan to take, or have
                            recently (within the last 3 months) taken opiate
                            pain medications and/or opiate-based street drugs?
                            */}
                            <GluedSingleChoiceQuestion
                                linkId="q-opiate-use"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Please include date range, name, dose, and
                            frequency. */}
                            <GluedLongOpenInputQuestion
                                linkId="q-opiate-use-followup"
                                variant="info"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Have you had bariatric (weight loss) surgery? */}
                            <GluedSingleChoiceQuestion
                                linkId="q-bariatric-surgery"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Please include date range and type of surgery. */}
                            <GluedLongOpenInputQuestion
                                linkId="q-bariatric-surgery-followup"
                                variant="info"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Do any of these apply to you? */}
                            <GluedMultiChoiceQuestion
                                linkId="q-medical-conditions"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Have there been any changes to your allergies
                            since your last visit with us? */}
                            <GluedSingleChoiceQuestion
                                linkId="q-allergies-changes"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Please list your current allergies, if any
                            (medication allergies, food allergies, etc.):
                            (enableWhen: q-allergies-changes = 'Yes') */}
                            <GluedLongOpenInputQuestion
                                linkId="q-allergies-list"
                                variant="info"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Have there been any changes to your medications
                            since your last visit with us? */}
                            <GluedSingleChoiceQuestion
                                linkId="q-medications-changes"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Please list your current daily medications,
                            including name, dose, and frequency. Also, please
                            indicate any medications that have started, stopped,
                            or changed since your last visit. (enableWhen:
                            q-medications-changes = 'Yes') */}
                            <GluedLongOpenInputQuestion
                                linkId="q-medications-list"
                                variant="info"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Have you experienced any of the following since
                            starting medication? */}
                            <GluedMultiChoiceQuestion
                                linkId="q-side-effects-start"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* This questionnaire is not monitored in real time
                            by a medical or mental health professional. If you
                            are experiencing a medical or mental health
                            emergency, please call 911 or go to your nearest
                            emergency room. If you are experiencing a mental
                            health crisis, please call the 988 Suicide & Crisis
                            Hotline. */}
                            <GluedDisplayQuestion
                                linkId="q-side-effects-warning"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* I have experienced the symptoms of fullness or
                            reflux */}
                            <GluedBooleanInputQuestion
                                linkId="q-symptoms-fullness-reflux"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Please classify your symptoms as: (enableWhen:
                            q-symptoms-fullness-reflux = true) */}
                            <GluedSingleChoiceQuestion
                                linkId="q-symptoms-fullness-reflux-classify"
                                variant="info"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Please explain your moderate or severe symptoms.
                            (enableWhen: q-symptoms-fullness-reflux-classify =
                            'Moderate' OR q-symptoms-fullness-reflux-classify =
                            'Severe') */}
                            <GluedLongOpenInputQuestion
                                linkId="q-symptoms-fullness-reflux-explain"
                                variant="info"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* I have experienced the symptoms of Nausea or
                            vomiting */}
                            <GluedBooleanInputQuestion
                                linkId="q-symptoms-nausea-vomiting"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Please classify your symptoms as: (enableWhen:
                            q-symptoms-nausea-vomiting = true) */}
                            <GluedSingleChoiceQuestion
                                linkId="q-symptoms-nausea-vomiting-classify"
                                variant="info"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Please explain your moderate or severe symptoms.
                            (enableWhen: q-symptoms-nausea-vomiting-classify =
                            'Moderate' OR q-symptoms-nausea-vomiting-classify =
                            'Severe') */}
                            <GluedLongOpenInputQuestion
                                linkId="q-symptoms-nausea-vomiting-explain"
                                variant="info"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* I have experienced the symptoms of Constipation */}
                            <GluedBooleanInputQuestion
                                linkId="q-symptoms-constipation"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Please classify your symptoms as: (enableWhen:
                            q-symptoms-constipation = true) */}
                            <GluedSingleChoiceQuestion
                                linkId="q-symptoms-constipation-classify"
                                variant="info"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Please explain your moderate or severe symptoms.
                            (enableWhen: q-symptoms-constipation-classify =
                            'Moderate' OR q-symptoms-constipation-classify =
                            'Severe') */}
                            <GluedLongOpenInputQuestion
                                linkId="q-symptoms-constipation-explain"
                                variant="info"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* I have experienced the symptoms of Abdominal pain */}
                            <GluedBooleanInputQuestion
                                linkId="q-symptoms-abdominal-pain"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Please classify your symptoms as: (enableWhen:
                            q-symptoms-abdominal-pain = true) */}
                            <GluedSingleChoiceQuestion
                                linkId="q-symptoms-abdominal-pain-classify"
                                variant="info"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Please explain your moderate or severe symptoms.
                            (enableWhen: q-symptoms-abdominal-pain-classify =
                            'Moderate' OR q-symptoms-abdominal-pain-classify =
                            'Severe') */}
                            <GluedLongOpenInputQuestion
                                linkId="q-symptoms-abdominal-pain-explain"
                                variant="info"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* I have experienced the symptoms of Food cravings */}
                            <GluedBooleanInputQuestion
                                linkId="q-symptoms-food-cravings"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Please classify your symptoms as: (enableWhen:
                            q-symptoms-food-cravings = true) */}
                            <GluedSingleChoiceQuestion
                                linkId="q-symptoms-food-cravings-classify"
                                variant="info"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Please explain your moderate or severe symptoms.
                            (enableWhen: q-symptoms-food-cravings-classify =
                            'Moderate' OR q-symptoms-food-cravings-classify =
                            'Severe') */}
                            <GluedLongOpenInputQuestion
                                linkId="q-symptoms-food-cravings-explain"
                                variant="info"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* I have experienced the symptoms of Headaches */}
                            <GluedBooleanInputQuestion
                                linkId="q-symptoms-headaches"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Please classify your symptoms as: (enableWhen:
                            q-symptoms-headaches = true) */}
                            <GluedSingleChoiceQuestion
                                linkId="q-symptoms-headaches-classify"
                                variant="info"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Please explain your moderate or severe symptoms.
                            (enableWhen: q-symptoms-headaches-classify =
                            'Moderate' OR q-symptoms-headaches-classify =
                            'Severe') */}
                            <GluedLongOpenInputQuestion
                                linkId="q-symptoms-headaches-explain"
                                variant="info"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* I have experienced the symptoms of Fatigue */}
                            <GluedBooleanInputQuestion
                                linkId="q-symptoms-fatigue"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Please classify your symptoms as: (enableWhen:
                            q-symptoms-fatigue = true) */}
                            <GluedSingleChoiceQuestion
                                linkId="q-symptoms-fatigue-classify"
                                variant="info"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Please explain your moderate or severe symptoms.
                            (enableWhen: q-symptoms-fatigue-classify =
                            'Moderate' OR q-symptoms-fatigue-classify =
                            'Severe') */}
                            <GluedLongOpenInputQuestion
                                linkId="q-symptoms-fatigue-explain"
                                variant="info"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* I have experienced other symptoms or side effects */}
                            <GluedBooleanInputQuestion
                                linkId="q-symptoms-other"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Please classify your symptoms as: (enableWhen:
                            q-symptoms-other = true) */}
                            <GluedSingleChoiceQuestion
                                linkId="q-symptoms-other-classify"
                                variant="info"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Please explain your moderate or severe symptoms.
                            (enableWhen: q-symptoms-other-classify = 'Moderate'
                            OR q-symptoms-other-classify = 'Severe') */}
                            <GluedLongOpenInputQuestion
                                linkId="q-symptoms-other-explain"
                                variant="info"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* I have had an Hospitalization since my last visit */}
                            <GluedSingleChoiceQuestion
                                linkId="q-hospitalization-since-last"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Please explain your hospital visit (enableWhen:
                            q-hospitalization-since-last = true) */}
                            <GluedLongOpenInputQuestion
                                linkId="q-hospitalization-explain"
                                variant="info"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Since starting medication, are you seeing benefit
                            or improvement in the following areas? */}
                            <GluedMultiChoiceQuestion
                                linkId="q-medication-benefit-improvement"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* What is your current weight in pounds (lbs)? */}
                            <GluedOpenInputQuestion
                                linkId="q-current-weight"
                                number
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* How many injections or weeks (if on oral
                            medication) of the current level of medication have
                            you completed? */}
                            <GluedSingleChoiceQuestion
                                linkId="q-injections-weeks-completed"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* When was your last dose of medication? */}
                            <GluedSingleChoiceQuestion
                                linkId="q-last-dose-date"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* What is your current or average blood pressure */}
                            <GluedSingleChoiceQuestion
                                linkId="q-blood-pressure"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* What is your current or average heart rate range? */}
                            <GluedSingleChoiceQuestion
                                linkId="q-heart-rate"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Do you have a preference with regard to
                            increasing, decreasing, or maintaining your current
                            dose? Please note that this does not guarantee the
                            preferred outcome and will be ultimately decided
                            based on the clinician’s evaluation. */}
                            <GluedSingleChoiceQuestion
                                linkId="q-dose-preference"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Please explain the reason for your preference.
                            (enableWhen: q-dose-preference != 'No preference') */}
                            <GluedLongOpenInputQuestion
                                linkId="q-dose-preference-reason"
                                variant="info"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Do you need a refill on your ondansetron (Zofran)
                            for nausea/vomiting medication? */}
                            <GluedSingleChoiceQuestion
                                linkId="q-refill-ondansetron"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Do you have any further information which you
                            would like the doctor to know? Please do not include
                            urgent or emergency medical information here, as
                            this is not reviewed immediately. */}
                            <GluedLongOpenInputQuestion
                                linkId="q-further-information"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <QuestionContainer>
                            {/* Please note that we strongly recommend that
                            patients of reproductive potential use an effective
                            method of contraception during treatment with a
                            GLP-1 medication and for at least 2 months
                            thereafter. For those taking Zepbound or Mounjaro
                            (tirzepatide), we recommend switching to a non-oral
                            contraceptive method or adding a barrier method of
                            contraception for four weeks after initiation and
                            for four weeks after each dose escalation. */}
                            <GluedDisplayQuestion
                                linkId="q-medhistory-followup-none-note"
                                {...questionFormGlue}
                            />
                        </QuestionContainer>

                        <Button
                            loading={isSubmitting}
                            sx={{ mt: 6, mb: 6, py: '20px' }}
                            onClick={onComplete}
                            disabled={!canProceed || isSubmitting}
                            fullWidth
                        >
                            Complete Customized Weight Management Refill
                        </Button>
                    </Form>
                    <LegalDisclaimer />
                </FormProvider>
            </LayoutGrid>
        </>
    )
}
